/**
 * Entity detail slide-over sheet.
 */

import { Building2, HelpCircle, FileText, Users } from "lucide-react";
import {
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import { Badge } from "@/ui/primitives/badge";
import { titleCaseCompanyName } from "@cvx/lib/companyName";
import type { EntityRow, EntityRelationships } from "../types";
import { TYPE_ICON, TYPE_COLOR, GROUP_CATEGORY_META } from "../constants";

// ---------------------------------------------------------------------------
// Section label helper
// ---------------------------------------------------------------------------

function SectionLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider text-muted-foreground ${className ?? ""}`}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EntityDetailSheet({
  entity,
  relationships,
  onDocumentClick,
}: {
  entity: EntityRow;
  relationships: EntityRelationships | undefined;
  onDocumentClick?: (documentId: string) => void;
}) {
  const Icon = TYPE_ICON[entity.type] ?? HelpCircle;
  const color = TYPE_COLOR[entity.type] ?? TYPE_COLOR.unknown;
  const siblings = relationships?.siblings ?? [];
  const associatedGroups = relationships?.associatedGroups ?? [];
  const hasSiblings = siblings.length > 0;
  const hasAssociated = associatedGroups.length > 0;
  const parentEntity = relationships?.parentEntity;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <SheetHeader className="gap-3">
        <div className="flex items-center gap-3">
          <div className={`shrink-0 rounded-lg border p-2 ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <SheetTitle className="text-lg leading-tight">
              {titleCaseCompanyName(entity.name)}
            </SheetTitle>
            <SheetDescription className="mt-1 flex items-center gap-2">
              <Badge
                variant="outline"
                size="sm"
                className={`capitalize ${color} border`}
              >
                {entity.type}
              </Badge>
              {entity.isPrimary && (
                <Badge color="indigo" variant="soft" size="sm">
                  Primary Entity
                </Badge>
              )}
              {entity.isCurrent === false && (
                <Badge
                  variant="outline"
                  size="sm"
                  className="text-muted-foreground"
                >
                  Former
                </Badge>
              )}
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      {/* Detail — show per-role details for merged entities */}
      {entity.roles.length > 1 ? (
        <div>
          <SectionLabel>Roles</SectionLabel>
          <div className="mt-1.5 flex flex-col gap-2">
            {entity.roles.map((role, ri) => {
              const meta =
                GROUP_CATEGORY_META[role.groupCategory] ??
                GROUP_CATEGORY_META.other;
              const RoleIcon = meta.icon;
              return (
                <div
                  key={role.groupCategory + ri}
                  className="flex items-start gap-2.5 rounded-lg border bg-muted/30 px-3 py-2"
                >
                  <RoleIcon
                    className={`mt-0.5 h-4 w-4 shrink-0 ${meta.color}`}
                  />
                  <div className="min-w-0">
                    <span className="text-sm font-medium">{meta.label}</span>
                    {role.detail && (
                      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                        {role.detail}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        entity.detail && (
          <div>
            <SectionLabel>Detail</SectionLabel>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/80">
              {entity.detail}
            </p>
          </div>
        )
      )}

      {/* Identifiers */}
      {(entity.acn || entity.abn) && (
        <div className="flex gap-6">
          {entity.acn && (
            <div>
              <SectionLabel>ACN</SectionLabel>
              <p className="mt-1 font-mono text-sm tabular-nums">
                {entity.acn}
              </p>
            </div>
          )}
          {entity.abn && (
            <div>
              <SectionLabel>ABN</SectionLabel>
              <p className="mt-1 font-mono text-sm tabular-nums">
                {entity.abn}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Relationship to parent */}
      {parentEntity && (
        <div>
          <SectionLabel>Relationship</SectionLabel>
          <div className="mt-1.5 flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2.5">
            <Building2 className="h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0">
              <p className="text-sm">
                <span className="capitalize">
                  {[...new Set(entity.roles.map((r) => r.groupCategory))]
                    .map((cat) => cat.replace(/_/g, " "))
                    .join(" & ")}
                </span>
                {" of "}
                <span className="font-semibold">{parentEntity.name}</span>
              </p>
              {parentEntity.acn && (
                <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                  ACN {parentEntity.acn}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Group context */}
      <div>
        <SectionLabel>Group</SectionLabel>
        <p className="mt-1 text-sm text-foreground/70">{entity.group}</p>
      </div>

      {/* Source documents */}
      {entity.sourceDocuments.length > 0 && (
        <div>
          <SectionLabel>
            Source Documents ({entity.sourceDocuments.length})
          </SectionLabel>
          <div className="mt-1.5 flex flex-col gap-1.5">
            {entity.sourceDocuments.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => onDocumentClick?.(doc.id)}
                className="flex cursor-pointer items-center gap-2 rounded-lg border bg-background px-3 py-2 text-left text-sm text-primary transition-colors hover:border-primary/30 hover:bg-primary/5"
              >
                <FileText className="h-4 w-4 shrink-0" />
                <span className="truncate">{doc.fileName}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Divider before relationships */}
      {(hasAssociated || hasSiblings) && <div className="border-t pt-2" />}

      {/* Associated groups (for companies) */}
      {hasAssociated &&
        associatedGroups.map((group) => {
          const GroupIcon = group.icon;
          return (
            <div key={group.category}>
              <div className="mb-2 flex items-center gap-2">
                <GroupIcon className={`h-4 w-4 ${group.color}`} />
                <SectionLabel className="mb-0">{group.label}</SectionLabel>
                <Badge variant="secondary" size="sm">
                  {group.entities.length}
                </Badge>
              </div>
              <div className="flex flex-col gap-1">
                {group.entities.map((related, ri) => {
                  const RIcon = TYPE_ICON[related.type] ?? HelpCircle;
                  const rColor = TYPE_COLOR[related.type] ?? TYPE_COLOR.unknown;
                  return (
                    <div
                      key={related.name + ri}
                      className="flex items-center gap-2.5 rounded-lg border bg-background px-3 py-2 text-sm transition-colors hover:bg-muted/30"
                    >
                      <div
                        className={`shrink-0 rounded-md border p-1 ${rColor}`}
                      >
                        <RIcon className="h-3 w-3" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                          {related.name}
                        </span>
                        {related.detail && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {related.detail}
                          </span>
                        )}
                      </div>
                      {related.isCurrent === false && (
                        <Badge
                          variant="outline"
                          className="shrink-0 px-1 py-0 text-[9px] text-muted-foreground"
                        >
                          Former
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

      {/* Siblings */}
      {hasSiblings && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <SectionLabel className="mb-0">Same Group</SectionLabel>
            <Badge variant="secondary" size="sm">
              {siblings.length}
            </Badge>
          </div>
          <div className="flex flex-col gap-1">
            {siblings.map((sib, si) => {
              const SIcon = TYPE_ICON[sib.type] ?? HelpCircle;
              const sColor = TYPE_COLOR[sib.type] ?? TYPE_COLOR.unknown;
              return (
                <div
                  key={sib.name + si}
                  className="flex items-center gap-2.5 rounded-lg border bg-background px-3 py-2 text-sm transition-colors hover:bg-muted/30"
                >
                  <div className={`shrink-0 rounded-md border p-1 ${sColor}`}>
                    <SIcon className="h-3 w-3" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {sib.name}
                    </span>
                    {sib.detail && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {sib.detail}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
